import React, { ChangeEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { File as FileComponent, FileUploadOptions, Link } from '@schuettflix/react-components';
import { PlusIcon } from '@schuettflix/icons-react';
import { cn } from '@/shared/utils/cn';
import { LocationAttachment } from '../domain/Attachment.ts';
import { DEFAULT_FILE_UPLOAD_CONFIG, UploadFile } from '@/shared/components/UploadSection/types.ts';
import { useFileUploadSession } from '@/shared/components/UploadSection/useFileUploadSession.tsx';

interface AttachmentUploadSectionProps {
    value: LocationAttachment[];
    onChange: (attachments: LocationAttachment[]) => void;
    disabled?: boolean;
    className?: string;
    uploadLinkText?: string;
}

export const LocationAttachmentUploadSection: React.FC<AttachmentUploadSectionProps> = ({
    value = [],
    onChange,
    disabled = false,
    uploadLinkText,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { uploads, addFilesToUpload, removeUpload } = useFileUploadSession({
        value,
        onChange,
    });

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            addFilesToUpload(files);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const FileUploader = () => (
        <div className="mt-2">
            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept={DEFAULT_FILE_UPLOAD_CONFIG.allowedMimeTypes.join(',')}
                disabled={disabled}
            />
            <Link
                tabIndex={0}
                prefix={<PlusIcon size="xs" />}
                className={cn('font-copy-md-strong pt-1', {
                    'pointer-events-none opacity-50': disabled,
                })}
                label={uploadLinkText || t('location.attachments.link')}
                onClick={handleUploadClick}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleUploadClick();
                    }
                }}
            />
        </div>
    );

    const getUploadOptions = useCallback(
        (uploadFile: UploadFile): FileUploadOptions | undefined => {
            if (uploadFile.status === 'uploading') {
                return {
                    progress: uploadFile.progress,
                    error: uploadFile.error,
                    onCancel: () => {
                        uploadFile.abortController?.abort();
                        removeUpload(uploadFile.remotePath);
                    },
                    onRetry: () => {},
                };
            }

            if (uploadFile.status === 'failed') {
                return {
                    progress: 100,
                    error: uploadFile.error,
                    onCancel: () => {
                        removeUpload(uploadFile.remotePath);
                    },
                    onRetry: () => {
                        const fileToRetry = uploadFile.file as File;
                        removeUpload(uploadFile.remotePath);
                        addFilesToUpload([fileToRetry]);
                    },
                };
            }

            return undefined;
        },
        [removeUpload, addFilesToUpload]
    );

    return (
        <div>
            <div className="flex flex-col gap-4">
                {uploads.length > 0 &&
                    uploads.map((file, index) => {
                        return (
                            <FileComponent
                                file={file.file}
                                key={index}
                                upload={getUploadOptions(file)}
                                thumbnailImageUrl={file.url ? `${file.url}?size=logo` : undefined}
                                onDelete={() => removeUpload(file.remotePath)}
                            />
                        );
                    })}
            </div>
            <FileUploader />
        </div>
    );
};
